/**
 * layout - the data-source page's binding of the shared detail-page surface.
 *
 * A data source has four panes over its canvas:
 *
 *   context    what this source IS - status, indexer, counts, keywords  (left)
 *   assistant  a chat against the indexed corpus                        (right)
 *   access     who this source is shared with                           (right)
 *   activity   the audit log                                            (right)
 *
 * The centre is the CANVAS - the tab bar and whichever tab is open (Details,
 * Files) - with the panes floating over its edges.
 *
 * WHY TEST STOPPED BEING A TAB. "Test" was a full-width tab holding a chat
 * transcript, so asking the source a question meant leaving the file list you
 * were asking about, and coming back lost the conversation with the unmount.
 * A chat read against the work is exactly the pane pattern - the matter
 * workspace's Assistant is the same shape - so it docks right and the canvas
 * keeps the two tabs that are genuinely about the record.
 *
 * WHERE THE TRANSCRIPT LIVES. Not here, and not in the pane: the page owns
 * `chatHistory` and hands the pane a controlled view of it. A pane body is
 * remounted when its pane is dragged to the other dock, and a conversation
 * that vanished on a dock move would be a worse bug than the tab it replaced.
 * Same reason the reindex socket subscription and the SSE upload live on the
 * page - see `AIStudioIndexDetailPage`.
 *
 * WHY ENTITY SCOPE. A source someone is curating (Files open, upload running)
 * and one someone is interrogating (Assistant open, transcript deep) want
 * different panes open, and a single key would make every data source inherit
 * whichever was arranged last. So the data-source uuid is in the storage key.
 *
 * NO LEGACY KEYS. This surface is new; nothing was ever written for it.
 *
 * NO DETAIL PANE AND NO READER. The page edits its record inline on the
 * Details tab and selects no row, and a file still opens in the shared
 * `reader` side panel at 65% - converting that is its own change, with its
 * own blob/streaming questions. So it binds the selection-free hook and
 * declares neither pane: the README's rule is to declare only the panes the
 * page actually produces.
 */

import {
  PanelBounds,
  PanelLayout as GenericLayout,
  PanelSlot as GenericSlot,
} from '../../../shared/surface/panelLayout';
import defineDetailSurface, {
  DetailPaneConfig,
} from '../../../shared/surface/defineDetailSurface';

export type DataSourcePanelId = 'context' | 'assistant' | 'access' | 'activity';

/** The canvas's tabs. Labels are the page's - see `AIStudioIndexDetailPage`. */
export type DataSourceTab = 'Details' | 'Files';

export const DATA_SOURCE_TABS: DataSourceTab[] = ['Details', 'Files'];

export type DataSourcePanelSlot = GenericSlot<DataSourcePanelId>;
export type DataSourcePanelLayout = GenericLayout<DataSourcePanelId>;

export const DATA_SOURCE_PANEL_IDS: DataSourcePanelId[] = [
  'context',
  'assistant',
  'access',
  'activity',
];

const DATA_SOURCE_PANES: Record<DataSourcePanelId, DetailPaneConfig> = {
  /*
    Open by default: status, indexer state, document count and size were the
    Details tab's metadata grid, and they are what the Files tab never had —
    "is this thing finished indexing" is the question asked while looking at
    the files, not instead of them.
  */
  context: {
    label: 'Context',
    bounds: { min: 260, max: 720 },
    dock: 'left', index: 0, width: 320, visible: true, rail: true,
  },
  /*
    The chat leads the right stack, closed until asked for - the same
    treatment the matter Assistant gets. A floor of 360 because a transcript
    narrower than that stops being readable.
  */
  assistant: {
    label: 'Test',
    bounds: { min: 360, max: 900 },
    dock: 'right', index: 0, width: 420, visible: false, rail: true,
  },
  /*
    ACCESS. Who a source is shared with was reachable only from the overflow
    menu, and only as the write panel - there was no way to LOOK at the list,
    which on a corpus of firm documents is the question asked most often. The
    page also carried three sharing props into a details panel that never read
    them; those are gone. See `DataSourceDetailsPanel`.
  */
  access: {
    label: 'Access',
    bounds: { min: 240, max: 640 },
    dock: 'right', index: 1, width: 300, visible: false, rail: true,
  },
  activity: {
    label: 'Activity',
    bounds: { min: 280, max: 760 },
    dock: 'right', index: 2, width: 340, visible: false, rail: true,
  },
};

export const DATA_SOURCE_SURFACE = defineDetailSurface<DataSourcePanelId, DataSourceTab>({
  kind: 'detail',
  // Also the `data-testid` prefix for the shell: `datasource-surface`, etc.
  name: 'datasource',
  scope: 'entity',
  ids: DATA_SOURCE_PANEL_IDS,
  panes: DATA_SOURCE_PANES,
  tabs: DATA_SOURCE_TABS,
});

/* ── derived, for call sites and tests that want the plain records ── */

export const DATA_SOURCE_PANEL_LABELS: Record<DataSourcePanelId, string> =
  DATA_SOURCE_SURFACE.labels;
export const DATA_SOURCE_PANEL_BOUNDS: Record<DataSourcePanelId, PanelBounds> =
  DATA_SOURCE_SURFACE.bounds;
export const DATA_SOURCE_DEFAULT_LAYOUT: DataSourcePanelLayout =
  DATA_SOURCE_SURFACE.defaults;
